'use client';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { trpc } from '@/lib/trpc';

/**
 * Tranche K — what the browser shows while the platform is down.
 *
 * Plan: `docs/superpowers/plans/admin-console/K-remote-control.md` (K3).
 *
 * Without this, maintenance mode reaches a visitor as the raw token
 * `maintenance` rendered wherever that page happens to print `error.message`
 * — the api throws `SERVICE_UNAVAILABLE` with a machine-readable token, and
 * `CONVENTIONS.md` tells every screen to surface `error.message` verbatim.
 * Correct for a real error, useless as a sentence.
 *
 * There is deliberately NO version floor here. The web app reports its version
 * as a short git SHA, which has no order — and a browser cannot be out of
 * date: it fetches the current build on every load. See `FLOORED_PLATFORMS`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ TWO EXEMPTIONS, AND THE FIRST ONE IS A LOCKOUT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `/admin` is exempt, and it is not a convenience. The server exempts platform
 * admins from the gate, so an admin's own requests keep working — but
 * `config.get` still answers "maintenance is on", truthfully, to everyone. A
 * client-side gate that believed it without checking the route would paint the
 * maintenance screen over `/admin/config`: the one screen that can turn
 * maintenance off, hidden by maintenance being on. The switch would be a
 * one-way door from the browser even though the API was perfectly willing.
 *
 * `/login` is exempt for the matching reason: an admin whose session expired
 * during the window has to be able to sign back in to reach that screen.
 *
 * ⚠️ It renders `children` while the query is loading and forever if the query
 * fails. A config that cannot be read means "carry on", the same rule the api
 * and the phone follow — see `platform-config.service.ts`.
 */

/** Route prefixes the maintenance screen must never cover. */
export const MAINTENANCE_EXEMPT_PREFIXES: readonly string[] = ['/admin', '/login'];

export function isMaintenanceExempt(pathname: string | null): boolean {
  if (!pathname) return true; // Unknown route → do not hide anything.
  return MAINTENANCE_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function MaintenanceGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();

  // `retry: 1` matches the console's convention: a Cloud Run cold start must
  // not read as a failure. `staleTime` is short because this is the one query
  // whose answer changes during an incident.
  const config = trpc.config.get.useQuery(undefined, {
    retry: 1,
    staleTime: 15_000,
    // Poll only while it is telling us we are down, so the page comes back on
    // its own when the window ends.
    refetchInterval: (q) => (q.state.data?.maintenance.active ? 20_000 : false),
  });

  const active = config.data?.maintenance.active === true;
  if (!active || isMaintenanceExempt(pathname)) return <>{children}</>;

  return (
    <main
      dir="rtl"
      lang="he"
      data-testid="maintenance-screen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center',
        background: 'var(--bg-canvas, #ffffff)',
        color: 'var(--text-primary, #141414)',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>אנחנו עושים תחזוקה</h1>
      <p style={{ margin: 0, color: 'var(--text-tertiary, #5a5a5a)', maxWidth: '32rem' }}>
        {/* The admin's own message when there is one, our copy when there is
            not. Never both, and never a blank screen. */}
        {config.data?.maintenance.message ?? 'השירות יחזור לפעול בקרוב. תודה על הסבלנות.'}
      </p>
    </main>
  );
}
